/**
 * The Competition Committee's year, as four phases and one gate.
 *
 * THE GATE IS THE DESIGN. Voting is reachable on the item a commissioner has opened, in the
 * meeting, and nowhere else. Everything else here exists to make that one rule checkable:
 *
 *   - Before the docket is published there is nothing to vote on.
 *   - After it is published and before the meeting opens, the docket is READABLE and UNVOTABLE.
 *     A GM may record a private provisional vote, which is not a vote and never counts.
 *   - In the meeting exactly one item is open. Earlier items are closed, later ones are locked.
 *   - After commit, the record is history and nothing is votable at all.
 *
 * A docket everybody can vote through in advance is a docket nobody argues about, and the meeting
 * is the argument. That is why `canVote` refuses on four separate grounds rather than on one.
 *
 * THE PHASE IS DERIVED, NEVER SET. The prototype has a phase switcher; it is a prototype
 * affordance and there is deliberately no way to choose a phase here. Publishing the docket moves
 * you to phase 2, opening the first item to phase 3, committing to phase 4 — each of them an act
 * with its own meaning, none of them a dropdown.
 */

import type { Docket, GmId, RuleProposal } from '../store/types.js';
import { tallyFrom, tallyOutcome, type Vote } from './proposals.js';

export type Phase = 'writing' | 'docket' | 'meeting' | 'committed';

export const PHASE_LABEL: Readonly<Record<Phase, string>> = {
  writing: 'Writing',
  docket: 'Docket published',
  meeting: 'The meeting',
  committed: 'Committed to record',
};

/** Which of the four the league is actually in, from the docket alone. */
export function phaseOfMeeting(docket: Docket | null | undefined): Phase {
  if (!docket) return 'writing';
  if (docket.committedAt) return 'committed';
  if (docket.meetingOpenedAt) return 'meeting';
  if (docket.publishedAt) return 'docket';
  return 'writing';
}

/** Where one item stands, in the meeting. */
export type ItemState = 'closed' | 'live' | 'locked';

export function itemState(id: string, docket: Docket | null | undefined): ItemState {
  if (!docket) return 'locked';
  if (docket.openItemId === id) return 'live';
  if ((docket.closedIds ?? []).includes(id)) return 'closed';
  return 'locked';
}

export interface VoteGate {
  /** The only thing a caller should branch on. */
  allowed: boolean;
  /** Why not, in the words the screen shows. Empty when allowed. */
  why: string;
}

/**
 * MAY THIS PERSON VOTE ON THIS ITEM, RIGHT NOW.
 *
 * Four refusals, deliberately separate, because they are four different things to tell somebody
 * and collapsing them into one boolean is how "nothing happens when I press it" gets shipped.
 */
export function canVote(
  { phase, item, recorded }: { phase: Phase; item: ItemState; recorded?: boolean },
): VoteGate {
  if (phase === 'committed') {
    return { allowed: false, why: 'The record is committed. Nothing can be voted on now.' };
  }
  if (phase !== 'meeting') {
    return {
      allowed: false,
      why: phase === 'writing'
        ? 'The docket is not published yet.'
        : 'The docket is published but the meeting has not opened. Nothing is votable yet.',
    };
  }
  if (item === 'locked') {
    return { allowed: false, why: 'The meeting has not reached this item.' };
  }
  if (item === 'closed') {
    return {
      allowed: false,
      why: recorded
        ? 'This item is recorded. A commissioner can reopen it.'
        : 'This item is closed.',
    };
  }
  return { allowed: true, why: '' };
}

/**
 * Open an item, closing whatever was open. Returns the docket to save.
 *
 * Opening the FIRST item is what starts the meeting, which is why `meetingOpenedAt` is set here
 * rather than by a separate "start the meeting" button — a meeting that has started but is on no
 * item is a state with nothing to show and nothing to do.
 */
export function openItem(docket: Docket, id: string, now: string): Docket {
  const closing = docket.openItemId && docket.openItemId !== id ? [docket.openItemId] : [];
  const closed = [...new Set([...(docket.closedIds ?? []), ...closing])].filter((x) => x !== id);
  return {
    ...docket,
    openItemId: id,
    closedIds: closed,
    meetingOpenedAt: docket.meetingOpenedAt ?? now,
    openedAt: now,
    updatedAt: now,
  };
}

/** Close the open item without opening another — the end of the last vote. */
export function closeItem(docket: Docket, now: string): Docket {
  if (!docket.openItemId) return docket;
  const { openedAt: _stopped, ...rest } = docket;
  return {
    ...rest,
    openItemId: undefined,
    closedIds: [...new Set([...(docket.closedIds ?? []), docket.openItemId])],
    updatedAt: now,
  };
}

/**
 * TABLE THE OPEN ITEM — set it down without deciding it.
 *
 * `closeItem` marks an item `closed`, which in this app means "the room has dealt with it": it
 * sits behind the meeting and `commitPlan` counts it among the things awaiting an outcome. That is
 * the right ending for a vote that happened. It is the wrong ending for one that did not.
 *
 * Tim opened an item by accident on 25 August and there was no way back. Every exit from `live`
 * led forwards — record an outcome, or close it as though the room had finished with it — so the
 * only honest options were to invent a vote or to leave the meeting parked on the wrong item.
 *
 * Tabling returns it to `locked`, which is exactly where it was before it was opened: no outcome,
 * no place in `closedIds`, and openable again whenever the room comes back to it. VOTES ALREADY
 * CAST ARE KEPT. Discarding them would be the app deciding that an accidental open erases what
 * people had said, and a tabled item that reopens with its votes intact is both recoverable and
 * visibly un-recorded — `commitPlan` still reports it as having no outcome, which is true.
 *
 * AND IT UNDOES THE START OF THE MEETING, which the first version did not, and that omission was
 * the whole point of the feature going unmet.
 *
 * `openItem` sets `meetingOpenedAt` as a side effect — opening the first item IS starting the
 * meeting, because a meeting that has started but is on no item has nothing to show. Tabling put
 * the item back and left that timestamp standing, so `phaseOfMeeting` went on answering
 * "meeting" for ever after. Tim's accidental tap on 25 August moved the league from phase 2 to
 * phase 3 permanently: every item read Locked, "the meeting has not reached this item", and the
 * private pre-meeting vote — gated on phase 2 — disappeared from every GM's screen with no way
 * back. Half an undo is worse than none, because the state it leaves is one nothing can name.
 *
 * So: if tabling leaves NO trace of a meeting having happened — nothing open, nothing closed,
 * nothing recorded — then no meeting happened, and the timestamp goes with the item. If anything
 * has been closed or recorded then the meeting genuinely started, and `meetingOpenedAt` is a fact
 * about the evening that tabling one item does not get to erase.
 */
export function tableItem(
  docket: Docket,
  now: string,
  /** The agenda, so "has anything actually been decided" is a question this can answer. */
  agenda: ReadonlyArray<{ recordedAt?: string }> = [],
): Docket {
  if (!docket.openItemId) return docket;
  const { openedAt: _stopped, ...rest } = docket;
  const next: Docket = { ...rest, openItemId: undefined, updatedAt: now };
  return meetingHappened(next, agenda) ? next : notStarted(next);
}

/**
 * HAS A MEETING ACTUALLY TAKEN PLACE, as opposed to an item having been opened.
 *
 * `meetingOpenedAt` is written by `openItem` as a side effect of opening the first item, so on
 * its own it cannot tell the difference between a committee sitting down and somebody's thumb.
 * Something CLOSED or something RECORDED can: both are things only the room does.
 *
 * Kept as one predicate because two callers need the same answer and a second copy of this rule
 * would be a second opinion about whether an evening happened.
 */
export function meetingHappened(
  docket: Docket,
  agenda: ReadonlyArray<{ recordedAt?: string }> = [],
): boolean {
  return (docket.closedIds ?? []).length > 0 || agenda.some((p) => p.recordedAt);
}

/** The docket with the meeting un-started. Never call without asking `meetingHappened` first. */
export function notStarted(docket: Docket): Docket {
  const { meetingOpenedAt: _dropped, ...rest } = docket;
  return rest as Docket;
}

/**
 * UN-START A MEETING THAT WAS OPENED BY ACCIDENT (Tim, 26 August, during the committee).
 *
 * `tableItem` already does this as a side effect — set down the only open item and, if nothing has
 * actually been decided, the docket goes back to phase 2. That was the right safety net and the
 * wrong affordance: it is on the ITEM, it is called "Table for now", and a commissioner who has
 * just put the room on ten screens by mistake is not looking for a button about an item. He is
 * looking for the way back from the thing he pressed, which is the console.
 *
 * So this is the same rule with its own name, in its own place. Both paths ask `meetingHappened`,
 * so they cannot disagree about whether an evening occurred.
 *
 * IT IS NOT AVAILABLE ONCE ANYTHING HAS BEEN DECIDED, and that is the whole gate. A closed item or
 * a recorded outcome is something only the room does; after either, "this never happened" is a
 * claim about the evening that the evening contradicts. Reopening a vote is the tool from there,
 * and it leaves a trail.
 */
export function canUnstart(
  docket: Docket | null | undefined,
  agenda: ReadonlyArray<{ recordedAt?: string }> = [],
): VoteGate {
  if (!docket?.meetingOpenedAt) return { allowed: false, why: 'The meeting has not opened.' };
  if (docket.committedAt) {
    return { allowed: false, why: 'The record is committed. The docket is history now.' };
  }
  if (meetingHappened(docket, agenda)) {
    return {
      allowed: false,
      why: 'The committee has already decided something. Reopen that vote instead.',
    };
  }
  return { allowed: true, why: '' };
}

/**
 * Put the docket back to a published agenda with no meeting against it.
 *
 * Closes whatever was open — an un-started meeting cannot leave an item on the floor — and drops
 * `meetingOpenedAt` and `openedAt` with it. The order, the attendance register and every private
 * pre-meeting note are left exactly as they were: nothing about them was a consequence of the
 * meeting having opened.
 *
 * Refuses rather than guesses when `canUnstart` says no, so a caller that forgets to ask cannot
 * quietly rewrite an evening.
 */
export function unstartMeeting(
  docket: Docket,
  now: string,
  agenda: ReadonlyArray<{ recordedAt?: string }> = [],
): Docket {
  if (!canUnstart(docket, agenda).allowed) return docket;
  const { openedAt: _stopped, ...rest } = docket;
  return notStarted({ ...rest, openItemId: undefined, updatedAt: now });
}

/** A commissioner reopening a vote he has already closed. Never available after commit. */
export function reopenItem(docket: Docket, id: string, now: string): Docket {
  if (docket.committedAt) return docket;
  return openItem({ ...docket, closedIds: (docket.closedIds ?? []).filter((x) => x !== id) },
    id, now);
}

/**
 * Record a vote, keeping WHO CAST IT.
 *
 * `castBy` is the whole reason this is a function rather than a spread at the call site: a vote
 * recorded for an absent GM is that GM's vote and the commissioner's act, and the minutes have to
 * be able to say which. Passing `null` withdraws a vote and its provenance together — a vote whose
 * value is gone but whose `castBy` survives would render "recorded by Rajan" against nothing.
 */
export function recordVote(
  p: RuleProposal,
  voter: GmId,
  vote: 'yes' | 'no' | 'abstain' | null,
  by: GmId,
  now: string,
): RuleProposal {
  const votes = { ...(p.votes ?? {}) };
  const castBy = { ...(p.castBy ?? {}) };
  const castAt = { ...(p.castAt ?? {}) };
  if (vote === null) {
    delete votes[voter]; delete castBy[voter]; delete castAt[voter];
  } else {
    votes[voter] = vote; castBy[voter] = by; castAt[voter] = now;
  }
  return { ...p, votes, castBy, castAt, updatedAt: now };
}

/** True where the vote was pressed by somebody other than the man it belongs to. */
export const isProxy = (p: RuleProposal, voter: GmId): boolean => {
  const by = p.castBy?.[voter];
  return Boolean(by && by.toUpperCase() !== voter.toUpperCase());
};

/** "recorded by Rajan" / "clicked 8:04pm" — the provenance line under a name on the board. */
export function provenance(p: RuleProposal, voter: GmId, shortName: (g: string) => string): string {
  if (!p.votes?.[voter]) return '';
  if (isProxy(p, voter)) return `recorded by ${shortName(p.castBy![voter]!)}`;
  const at = p.castAt?.[voter];
  if (!at) return 'clicked';
  const d = new Date(at);
  const h = d.getHours(); const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  return `clicked ${((h + 11) % 12) + 1}:${m}${ampm}`;
}

/**
 * A private provisional vote. Not a vote: it is stored on its own field, it carries a note, and
 * nothing that tallies ever reads it.
 */
export function recordProvisional(
  p: RuleProposal,
  gm: GmId,
  entry: { vote: 'yes' | 'no' | 'abstain'; note?: string; shared?: boolean } | null,
  now: string,
): RuleProposal {
  const provisional = { ...(p.provisional ?? {}) };
  if (entry === null) delete provisional[gm];
  else provisional[gm] = entry;
  return { ...p, provisional, updatedAt: now };
}

/**
 * WRITING DOWN WHAT THE ROOM JUST DECIDED — one function, because there are now two ways to do it.
 *
 * The item's own Record button and the commissioner console's primary both end here. They were
 * about to be two copies of the same six lines, and the failure mode of that is the worst one
 * this app has: two paths to one act that disagree about the threshold, on the night, in public.
 *
 * `no-quorum` is stored as `failed` because the proposal's own status has no such value — the
 * distinction survives in the tally and in `OUTCOME_LABEL`, which is where it is read from.
 */
export function recordedProposal(
  p: RuleProposal, represented: number, now: string,
): RuleProposal {
  const t = tallyFrom((p.votes ?? {}) as Record<string, Vote>, represented);
  const r = tallyOutcome(t, { isRevote: p.isRevote, proposerObjects: p.proposerObjects });
  return {
    ...p,
    status: r.outcome === 'no-quorum' ? 'failed' : r.outcome,
    recordedAt: now,
    updatedAt: now,
  };
}

export interface CommitPlan {
  /** Proposals that reached seven and become rules with 2026 ids. */
  intoRulebook: string[];
  /** Six-vote passes: in force for one season and back on next year's docket automatically. */
  oneSeason: string[];
  /** Provisional rules whose re-vote failed — they leave force. */
  outOfForce: string[];
  /** Everything that needs a build change before it is true in the app. */
  needsBuild: string[];
  /** True when nothing is left unvoted. Commit refuses otherwise. */
  ready: boolean;
  /** What is still in the way, for the button's disabled reason. */
  blocking: string;
}

/**
 * WHAT COMMIT ACTUALLY WRITES, stated before it is pressed.
 *
 * It is the one irreversible step on this page, and the design brief is explicit that it has to
 * say exactly what it does rather than "are you sure?". So the plan is computed from the docket
 * and shown; the button writes precisely what the plan lists.
 */
export function commitPlan(agenda: readonly RuleProposal[]): CommitPlan {
  const unvoted = agenda.filter((p) => !p.recordedAt);
  return {
    intoRulebook: agenda.filter((p) => p.status === 'passed').map((p) => p.id),
    oneSeason: agenda.filter((p) => p.status === 'provisional').map((p) => p.id),
    outOfForce: agenda.filter((p) => p.isRevote && p.status === 'failed').map((p) => p.id),
    needsBuild: agenda.filter((p) => (p.status === 'passed' || p.status === 'provisional')
      && Boolean(p.effect)).map((p) => p.id),
    ready: unvoted.length === 0 && agenda.length > 0,
    blocking: agenda.length === 0
      ? 'There is nothing on the docket.'
      : unvoted.length > 0
        ? `${unvoted.length} item${unvoted.length === 1 ? ' has' : 's have'} no recorded outcome yet.`
        : '',
  };
}

/**
 * MAY THE DOCKET BE TAKEN BACK, and it is a narrower question than it looks.
 *
 * Publishing is what tells nine people the agenda is settled, so unpublishing is not an undo —
 * it is a retraction, and there is exactly one window where it is honest. Before the meeting
 * opens the docket has been READ but nothing has been decided on it, so pulling it back costs
 * only the confusion of a second announcement. Once the first item opens, votes exist against a
 * running order, and once the record is committed the docket is history. Neither can be walked
 * back by clearing two fields, and pretending otherwise would leave votes pointing at an agenda
 * that no longer says what they were cast against.
 *
 * The reason it is needed at all: Tim published a docket on 25 August with one item on it, having
 * expected three. An agenda is the one thing here that is announced to everybody at a moment, and
 * a commissioner who announces the wrong one needs a way to say so that is not "publish it again
 * and hope people re-read it".
 */
export function canUnpublish(docket: Docket | null | undefined): VoteGate {
  if (!docket?.publishedAt) return { allowed: false, why: 'The docket is not published.' };
  if (docket.committedAt) {
    return { allowed: false, why: 'The record is committed. The docket is history now.' };
  }
  if (docket.meetingOpenedAt) {
    return {
      allowed: false,
      why: 'The meeting has opened. Votes have been cast against this running order.',
    };
  }
  return { allowed: true, why: '' };
}

/** The docket, unpublished. Everything else about it - order, attendance - is left alone. */
export function unpublished(docket: Docket): Docket {
  const { publishedAt, publishedBy, ...rest } = docket;
  /*
   * The meeting goes back with it, when there was not one. Taking the agenda back and leaving
   * `meetingOpenedAt` standing produces a docket that is unpublished and mid-meeting at once —
   * a state `phaseOfMeeting` reads as "writing" while the record says the committee sat down.
   */
  return meetingHappened(rest) ? rest : notStarted(rest);
}
